import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { 
  Bell, 
  ArrowLeft,
  RefreshCw,
  Users,
  CheckCircle,
  Clock,
  Calendar,
  User,
  Eye,
  EyeOff
} from 'lucide-react';
import { useLanguage } from '@/i18n/LanguageContext';

export const NotificationRecipientsPage = () => {
  const { t, isRTL } = useLanguage();
  const { notificationId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [notification, setNotification] = useState(null);
  const [recipients, setRecipients] = useState([]);
  const [summary, setSummary] = useState({ total: 0, read: 0, unread: 0 });

  useEffect(() => {
    fetchRecipients();
  }, [notificationId]);

  const fetchRecipients = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/notifications/${notificationId}/recipients`, {
        withCredentials: true
      });
      setNotification(response.data.notification);
      setRecipients(response.data.recipients);
      setSummary(response.data.summary);
    } catch (error) {
      console.error('Failed to fetch recipients:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (isoString) => {
    if (!isoString) return '-';
    const date = new Date(isoString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="outline"
          size="sm"
          onClick={() => navigate('/admin/notifications')}
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Bell className="w-7 h-7 text-indigo-600" />
            Notification Recipients
          </h1>
          <p className="text-gray-600 mt-1">View read status for each recipient</p>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="w-8 h-8 animate-spin text-indigo-600" />
        </div>
      ) : (
        <>
          {/* Notification Info Card */}
          {notification && (
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Users className="w-5 h-5 text-purple-600" />
                      Team: {notification.recipient_name}
                    </CardTitle>
                    <CardDescription className="mt-1">
                      Sent on {formatDate(notification.created_at)}
                    </CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Badge className="bg-indigo-100 text-indigo-700">
                      <Eye className="w-3 h-3 mr-1" />
                      {summary.read} Read
                    </Badge>
                    <Badge className="bg-amber-100 text-amber-700">
                      <EyeOff className="w-3 h-3 mr-1" />
                      {summary.unread} Unread
                    </Badge>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-gray-700">{notification.message}</p>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Recipients Table */}
          <Card>
            <CardHeader>
              <CardTitle>Recipients ({recipients.length})</CardTitle>
              <CardDescription>Staff members who received this notification</CardDescription>
            </CardHeader>
            <CardContent>
              {recipients.length === 0 ? (
                <div className="text-center py-8">
                  <Users className="w-12 h-12 mx-auto text-gray-300 mb-4" />
                  <p className="text-gray-500">No recipients found</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Staff Name</TableHead>
                      <TableHead>{t("staff.department")}</TableHead>
                      <TableHead>{t("common.status")}</TableHead>
                      <TableHead>Read Date & Time</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {recipients.map((recipient) => (
                      <TableRow key={recipient.staff_id}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <User className="w-4 h-4 text-gray-400" />
                            <span className="font-medium">{recipient.staff_name}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          {recipient.department || <span className="text-gray-400">-</span>}
                        </TableCell>
                        <TableCell>
                          {recipient.is_read ? (
                            <Badge className="bg-indigo-100 text-indigo-700">
                              <CheckCircle className="w-3 h-3 mr-1" />
                              Read
                            </Badge>
                          ) : (
                            <Badge className="bg-amber-100 text-amber-700">
                              <Clock className="w-3 h-3 mr-1" />
                              Unread
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          {recipient.is_read && recipient.read_at ? (
                            <div className="flex items-center gap-2 text-sm text-gray-600">
                              <Calendar className="w-4 h-4 text-gray-400" />
                              {formatDate(recipient.read_at)}
                            </div>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

export default NotificationRecipientsPage;
